import { defineElement, FC, onDidLoad, useElement, useProp } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { BPDFunctionalSvg } from '@atoms/svg/svg';
import {
    getUserAreas,
    getUserInfo,
    handleGTM,
    isLoggedIn,
    login,
    UserAreaProject,
    UserInfo,
    userStore,
} from '@store/user';
import { isMobile } from '@utilities/media-query';

const PROFILE_DROPDOWN_OPEN_CLASS = 'profile-button--dropdown-open';

export interface ProfileButtonElement extends HTMLElement {
    useMehNavigation: boolean;
    mijnHomeUrl: string;
    mijnAccountUrl: string;
    accountNavigationLinks: string;
    dropdown: HTMLElement;
}

const ProfileButton: FC<ProfileButtonElement> = ({ element }) => {
    useProp<boolean>('useMehNavigation', false); // feature flag

    useProp<string>('mijnHomeUrl', '');
    useProp<string>('mijnAccountUrl', '');
    useProp<string>('accountNavigationLinks', '');

    useElement<HTMLElement>('[js-hook-profile-button-dropdown]', { as: 'dropdown' });

    let userInfo: UserInfo | undefined = undefined;
    let userAreas: UserAreaProject[] | undefined = undefined;

    onDidLoad(() => {
        userStore.subscribe(handleLoginChange, ['loggedIn']);
        handleLoginChange();
    });

    function handleLoginChange() {
        userInfo = getUserInfo();
        userAreas = getUserAreas();

        render();
        handleGTM();
    }

    function handleClick() {
        if (!isLoggedIn()) {
            login();
        } else if (isMobile()) {
            element.classList.toggle(PROFILE_DROPDOWN_OPEN_CLASS);
        }
    }

    function render() {
        const hasUserAreas = element.useMehNavigation && userAreas && userAreas.length > 1;

        element.innerHTML = '';
        element.append(
            <div class="c-profile-button u-flex u-flex-center u-flex-middle">
                <button
                    class="profile-button__inner u-flex u-flex-center u-flex-middle"
                    onClick={handleClick}>
                    {userInfo?.avatar && (
                        <figure class="profile-button__circle">
                            <img src={userInfo?.avatar} alt="Profile" />
                        </figure>
                    )}

                    {userInfo?.initials && !userInfo.avatar && (
                        <span class="profile-button__circle">
                            <span>{userInfo.initials}</span>
                        </span>
                    )}

                    {!userInfo?.avatar && !userInfo?.initials && (
                        <Fragment>
                            <div class="profile-button__icon">
                                <BPDFunctionalSvg name={'icons-user'} />
                            </div>

                            <span class="profile-button__label">Inloggen</span>
                        </Fragment>
                    )}
                </button>

                {isLoggedIn() && (
                    <div class="profile-button__dropdown" js-hook-profile-button-dropdown>
                        <bpd-profile-dropdown
                            full-name={userInfo?.displayName}
                            email={userInfo?.email}
                            mijn-home-url={element.mijnHomeUrl}
                            mijn-account-url={element.mijnAccountUrl}
                            use-meh-navigation={element.useMehNavigation}
                            account-navigation-links={element.accountNavigationLinks}
                            has-user-areas={hasUserAreas}
                        />
                    </div>
                )}
            </div>,
        );
    }
};

ProfileButton.props = {
    useMehNavigation: { type: Boolean }, // feature flag
    mijnHomeUrl: { type: String },
    mijnAccountUrl: { type: String },
    accountNavigationLinks: { type: String },
};

defineElement('bpd-profile-button', ProfileButton);
